import type { Position } from "@xyflow/react";
import { GEOMETRY } from "./canvas-geometry";
import { sideDir, simplifyOrthogonal, type Pt } from "./cable-routing";

/** Axis-aligned obstacle rect. `core` is the un-inflated body when the rect
 *  carries clearance. */
export type Rect = {
  x: number;
  y: number;
  w: number;
  h: number;
  core?: { x: number; y: number; w: number; h: number };
};

const { STUB, ELBOW_MARGIN, ZONE_WALL_PENALTY } = GEOMETRY;

/** Cap on grid lines per axis; squares into the A* cost (`MAX_LINES²` nodes ×
 *  4 arrival directions). Past the cap the lanes nearest the source→target span
 *  win. */
const MAX_LINES = 36;

/** How far outside the source→target box a lane may sit and still enter the
 *  grid: roughly two component widths plus padding. */
const WINDOW_MARGIN = 300;

/** Well above the router's own `BEND_PENALTY` (15): a grid search with a cheap
 *  bend staircases. */
const ESCAPE_BEND = 50;

const DX = [1, -1, 0, 0];
const DY = [0, 0, 1, -1];
const OPPOSITE = [1, 0, 3, 2];

function dirIndex(d: Pt): number {
  if (d.x > 0) return 0;
  if (d.x < 0) return 1;
  if (d.y > 0) return 2;
  return 3;
}

function distToInterval(v: number, lo: number, hi: number): number {
  if (v < lo) return lo - v;
  if (v > hi) return v - hi;
  return 0;
}

/** Grid lines for one axis. The `ELBOW_MARGIN` offset keeps a lane off the
 *  boundary itself — outside the body and clear of float noise. */
function axisLines(
  anchors: number[],
  rects: readonly Rect[],
  axis: "x" | "y",
  lo: number,
  hi: number,
): number[] {
  const winLo = lo - WINDOW_MARGIN;
  const winHi = hi + WINDOW_MARGIN;
  const set = new Set<number>(anchors);
  for (const r of rects) {
    const a = axis === "x" ? r.x : r.y;
    const b = axis === "x" ? r.x + r.w : r.y + r.h;
    const before = a - ELBOW_MARGIN;
    const after = b + ELBOW_MARGIN;
    if (before >= winLo && before <= winHi) set.add(before);
    if (after >= winLo && after <= winHi) set.add(after);
  }
  let list = Array.from(set);
  if (list.length > MAX_LINES) {
    const anchorSet = new Set(anchors);
    list.sort((p, q) => {
      // Anchors are mandatory — the search starts and ends on them.
      const dp = anchorSet.has(p) ? -1 : distToInterval(p, lo, hi);
      const dq = anchorSet.has(q) ? -1 : distToInterval(q, lo, hi);
      return dp - dq;
    });
    list = list.slice(0, MAX_LINES);
  }
  list.sort((p, q) => p - q);
  return list;
}

/** Marks every grid edge whose open span shares interior with `r`. `hMark` is
 *  indexed `row * (nx - 1) + col`, `vMark` `row * nx + col`. */
function markRect(
  r: Rect,
  xs: number[],
  ys: number[],
  hMark: Uint8Array,
  vMark: Uint8Array,
): void {
  const nx = xs.length;
  const ny = ys.length;
  const x0 = r.x;
  const x1 = r.x + r.w;
  const y0 = r.y;
  const y1 = r.y + r.h;
  const EPS = 1e-6;
  for (let j = 0; j < ny; j++) {
    const y = ys[j];
    if (y <= y0 + EPS || y >= y1 - EPS) continue;
    for (let i = 0; i < nx - 1; i++) {
      if (xs[i + 1] <= x0 + EPS || xs[i] >= x1 - EPS) continue;
      const idx = j * (nx - 1) + i;
      if (hMark[idx] < 255) hMark[idx]++;
    }
  }
  for (let i = 0; i < nx; i++) {
    const x = xs[i];
    if (x <= x0 + EPS || x >= x1 - EPS) continue;
    for (let j = 0; j < ny - 1; j++) {
      if (ys[j + 1] <= y0 + EPS || ys[j] >= y1 - EPS) continue;
      const idx = j * nx + i;
      if (vMark[idx] < 255) vMark[idx]++;
    }
  }
}

/** Pull `r`'s nearest edge back past each point so the rect no longer contains
 *  it. `null` when the pull-back would collapse the rect. */
function shrinkToExclude(r: Rect | null, ...points: Pt[]): Rect | null {
  let out: Rect | null = r;
  for (const p of points) out = shrinkOne(out, p);
  return out;
}

function shrinkOne(r: Rect | null, p: Pt): Rect | null {
  if (!r) return null;
  const left = p.x - r.x;
  const right = r.x + r.w - p.x;
  const top = p.y - r.y;
  const bottom = r.y + r.h - p.y;
  if (left <= 0 || right <= 0 || top <= 0 || bottom <= 0) return r;
  const EPS = 0.01;
  const min = Math.min(left, right, top, bottom);
  if (min <= EPS) return null;
  if (min === left) return { x: p.x + EPS, y: r.y, w: r.w - left - EPS, h: r.h };
  if (min === right) return { x: r.x, y: r.y, w: r.w - right - EPS, h: r.h };
  if (min === top) return { x: r.x, y: p.y + EPS, w: r.w, h: r.h - top - EPS };
  return { x: r.x, y: r.y, w: r.w, h: r.h - bottom - EPS };
}

class MinHeap {
  private pri: number[] = [];
  private val: number[] = [];

  get size(): number {
    return this.val.length;
  }

  push(p: number, v: number): void {
    this.pri.push(p);
    this.val.push(v);
    let i = this.val.length - 1;
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (this.pri[parent] <= this.pri[i]) break;
      this.swap(i, parent);
      i = parent;
    }
  }

  pop(): number {
    const top = this.val[0];
    const lastP = this.pri.pop()!;
    const lastV = this.val.pop()!;
    if (this.val.length > 0) {
      this.pri[0] = lastP;
      this.val[0] = lastV;
      let i = 0;
      for (;;) {
        const l = 2 * i + 1;
        const r = l + 1;
        let best = i;
        if (l < this.val.length && this.pri[l] < this.pri[best]) best = l;
        if (r < this.val.length && this.pri[r] < this.pri[best]) best = r;
        if (best === i) break;
        this.swap(i, best);
        i = best;
      }
    }
    return top;
  }

  private swap(a: number, b: number): void {
    const p = this.pri[a];
    this.pri[a] = this.pri[b];
    this.pri[b] = p;
    const v = this.val[a];
    this.val[a] = this.val[b];
    this.val[b] = v;
  }
}

/** Orthogonal route around every rect in `hard`/`self`, over the Hanan grid
 *  those rects induce.
 *
 *  Bodies must never be crossed, clearance may be: it searches with every
 *  obstacle's padding first, then against the bodies alone, because a dense
 *  layout often cannot respect clearance at all.
 *
 *  `null` when the endpoints are boxed in, leaving the caller its own
 *  best-effort route. */
export function planEscapeRoute(
  source: Pt,
  sourceSide: Position,
  target: Pt,
  targetSide: Position,
  hard: readonly Rect[],
  self: readonly Rect[] = [],
  soft: readonly Rect[] = [],
  sourceStub: number = STUB,
  targetStub: number = STUB,
): Pt[] | null {
  const sDir = sideDir(sourceSide);
  const tDir = sideDir(targetSide);
  const sa: Pt = {
    x: source.x + sDir.x * sourceStub,
    y: source.y + sDir.y * sourceStub,
  };
  const ta: Pt = {
    x: target.x + tDir.x * targetStub,
    y: target.y + tDir.y * targetStub,
  };
  const walls = [{ rects: soft, cost: ZONE_WALL_PENALTY }];

  // A rect that swallows a stub anchor blocks every edge out of it and strands
  // the search — OBSTACLE_PADDING (20) exceeds STUB (18), so a neighbour within
  // ~38 px covers it. Shrink such a rect, never drop it: it is usually the very
  // body the cable would run through.
  const inflated: Rect[] = [];
  let anyPadded = false;
  for (const r of [...hard, ...self]) {
    if (r.core !== undefined) anyPadded = true;
    const shrunk = shrinkToExclude(r, sa, ta);
    if (shrunk) inflated.push(shrunk);
  }
  const clear = search(source, sa, sDir, target, ta, tDir, inflated, walls);
  if (clear || !anyPadded) return clear;

  const cores: Rect[] = [];
  for (const r of [...hard, ...self]) {
    const shrunk = shrinkToExclude(r.core ?? r, sa, ta);
    if (shrunk) cores.push(shrunk);
  }
  return search(source, sa, sDir, target, ta, tDir, cores, walls);
}

function search(
  source: Pt,
  sa: Pt,
  sDir: Pt,
  target: Pt,
  ta: Pt,
  tDir: Pt,
  blockers: readonly Rect[],
  priced: readonly { rects: readonly Rect[]; cost: number }[],
): Pt[] | null {
  const loX = Math.min(sa.x, ta.x);
  const hiX = Math.max(sa.x, ta.x);
  const loY = Math.min(sa.y, ta.y);
  const hiY = Math.max(sa.y, ta.y);
  const gridRects = [...blockers];
  for (const g of priced) gridRects.push(...g.rects);
  const xs = axisLines([sa.x, ta.x], gridRects, "x", loX, hiX);
  const ys = axisLines([sa.y, ta.y], gridRects, "y", loY, hiY);
  const nx = xs.length;
  const ny = ys.length;
  if (nx < 2 && ny < 2) return null;

  const hBlocked = new Uint8Array(Math.max(0, nx - 1) * ny);
  const vBlocked = new Uint8Array(nx * Math.max(0, ny - 1));
  for (const r of blockers) markRect(r, xs, ys, hBlocked, vBlocked);
  let hCost: Float32Array | null = null;
  let vCost: Float32Array | null = null;
  for (const g of priced) {
    if (g.rects.length === 0) continue;
    const hm = new Uint8Array(hBlocked.length);
    const vm = new Uint8Array(vBlocked.length);
    for (const r of g.rects) markRect(r, xs, ys, hm, vm);
    hCost ??= new Float32Array(hBlocked.length);
    vCost ??= new Float32Array(vBlocked.length);
    for (let i = 0; i < hm.length; i++) if (hm[i]) hCost[i] += hm[i] * g.cost;
    for (let i = 0; i < vm.length; i++) if (vm[i]) vCost[i] += vm[i] * g.cost;
  }

  const xi = xs.indexOf(sa.x);
  const yi = ys.indexOf(sa.y);
  const xg = xs.indexOf(ta.x);
  const yg = ys.indexOf(ta.y);
  if (xi < 0 || yi < 0 || xg < 0 || yg < 0) return null;
  const startNode = yi * nx + xi;
  const goalNode = yg * nx + xg;

  const sDirIdx = dirIndex(sDir);
  const tDirIdx = dirIndex(tDir);
  // Arriving at `ta` along +tDir would double back on the target's stub, so
  // that direction is barred.
  const arriveIdx = OPPOSITE[tDirIdx];

  const nodeCount = nx * ny;
  const stateCount = nodeCount * 4;
  const dist = new Float64Array(stateCount).fill(Infinity);
  const parent = new Int32Array(stateCount).fill(-1);
  const done = new Uint8Array(stateCount);
  const heuristic = (node: number): number => {
    const x = xs[node % nx];
    const y = ys[(node / nx) | 0];
    return Math.abs(x - ta.x) + Math.abs(y - ta.y);
  };

  const heap = new MinHeap();
  const startState = startNode * 4 + sDirIdx;
  dist[startState] = 0;
  heap.push(heuristic(startNode), startState);

  let goalState = -1;
  while (heap.size > 0) {
    const state = heap.pop();
    if (done[state]) continue;
    done[state] = 1;
    const node = (state / 4) | 0;
    const dir = state % 4;
    if (node === goalNode && dir !== tDirIdx) {
      goalState = state;
      break;
    }
    const col = node % nx;
    const row = (node / nx) | 0;
    const g = dist[state];
    for (let nd = 0; nd < 4; nd++) {
      if (nd === OPPOSITE[dir]) continue;
      const ncol = col + DX[nd];
      const nrow = row + DY[nd];
      if (ncol < 0 || ncol >= nx || nrow < 0 || nrow >= ny) continue;
      let step: number;
      let extra: number;
      if (DX[nd] !== 0) {
        const edge = row * (nx - 1) + Math.min(col, ncol);
        if (hBlocked[edge] > 0) continue;
        step = Math.abs(xs[ncol] - xs[col]);
        extra = hCost ? hCost[edge] : 0;
      } else {
        const edge = Math.min(row, nrow) * nx + col;
        if (vBlocked[edge] > 0) continue;
        step = Math.abs(ys[nrow] - ys[row]);
        extra = vCost ? vCost[edge] : 0;
      }
      const nnode = nrow * nx + ncol;
      if (nnode === goalNode && nd === tDirIdx) continue;
      let cost = step + extra;
      if (nd !== dir) cost += ESCAPE_BEND;
      if (nnode === goalNode && nd !== arriveIdx) cost += ESCAPE_BEND;
      const nstate = nnode * 4 + nd;
      if (done[nstate]) continue;
      const ng = g + cost;
      if (ng >= dist[nstate]) continue;
      dist[nstate] = ng;
      parent[nstate] = state;
      heap.push(ng + heuristic(nnode), nstate);
    }
  }
  if (goalState < 0) return null;

  const pts: Pt[] = [];
  for (let s = goalState; s >= 0; s = parent[s]) {
    const node = (s / 4) | 0;
    pts.push({ x: xs[node % nx], y: ys[(node / nx) | 0] });
  }
  pts.reverse();
  return simplifyOrthogonal([source, ...pts, target]);
}
